import { geoMercator } from "d3-geo";
import { useEffect, useMemo, useState } from "react";
import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { numericToAlpha2 } from "../components/iso-country-codes";

const GEO_URL = "/world-countries.json";

// Default projection config (world view)
const DEFAULT_CONFIG = {
  scale: 100,
  center: [0, 20] as [number, number],
};

// Constraints to avoid extreme zoom levels
const MIN_SCALE = 60;
const MAX_SCALE = 10000;

// Padding as pixels on each side
const PADDING = 10;

interface MapProjectionConfig {
  scale: number;
  center: [number, number];
}

interface UseMapAutoFitOptions {
  containerWidth: number;
  containerHeight: number;
  countryCodes: Set<string>;
  enabled?: boolean;
}

interface UseMapAutoFitResult {
  projectionConfig: MapProjectionConfig;
  isLoading: boolean;
}

type WorldTopology = Topology<{
  countries: GeometryCollection<{ name: string }>;
}>;

/**
 * Hook to calculate optimal map projection config to fit all countries with data.
 * Uses d3-geo's fitExtent to get a properly fitted projection, then extracts
 * the center by inverting the viewport center point.
 */
export function useMapAutoFit({
  containerWidth,
  containerHeight,
  countryCodes,
  enabled = true,
}: UseMapAutoFitOptions): UseMapAutoFitResult {
  const [geoData, setGeoData] = useState<WorldTopology | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load TopoJSON data once
  useEffect(() => {
    fetch(GEO_URL)
      .then((res) => res.json())
      .then((data: WorldTopology) => {
        setGeoData(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  const projectionConfig = useMemo((): MapProjectionConfig => {
    // Return default if disabled, no data, or container not ready
    if (
      !enabled ||
      !geoData ||
      countryCodes.size === 0 ||
      containerWidth <= 0 ||
      containerHeight <= 0
    ) {
      return DEFAULT_CONFIG;
    }

    try {
      // Convert TopoJSON to GeoJSON features
      const countriesGeoJson = feature(
        geoData,
        geoData.objects.countries
      ) as FeatureCollection<Geometry, { name: string }>;

      // Filter to only countries with data
      const relevantFeatures = countriesGeoJson.features.filter((f) => {
        const numericId = (f as Feature & { id?: string }).id;
        if (!numericId) return false;
        const alpha2 = numericToAlpha2[numericId];
        return alpha2 && countryCodes.has(alpha2);
      });

      if (relevantFeatures.length === 0) {
        return DEFAULT_CONFIG;
      }

      // Create a feature collection for all relevant countries
      const combinedCollection: FeatureCollection = {
        type: "FeatureCollection",
        features: relevantFeatures,
      };

      // Use d3's fitExtent to get a properly fitted projection
      // fitExtent adjusts scale and translate to fit the features within the extent
      const fittedProjection = geoMercator().fitExtent(
        [
          [PADDING, PADDING],
          [containerWidth - PADDING, containerHeight - PADDING],
        ],
        combinedCollection
      );

      // Get the scale from the fitted projection
      const scale = fittedProjection.scale();

      // Get the translate from the fitted projection
      const [tx, ty] = fittedProjection.translate();

      // react-simple-maps always sets translate to [width/2, height/2]
      // and uses 'center' to offset from there.
      // We need to find what geographic point should be at [width/2, height/2]
      // to achieve the same result as the fitted projection.
      //
      // In d3-geo Mercator with center=[0,0]:
      //   pixel = scale * mercator(geo) + translate
      //
      // The fitted projection has its center at [0,0] by default, so:
      //   pixel = scale * mercator(geo) + [tx, ty]
      //
      // For react-simple-maps with center=[cx, cy]:
      //   pixel = scale * (mercator(geo) - mercator([cx, cy])) + [width/2, height/2]
      //
      // To find the center, we invert [width/2, height/2] using the fitted projection
      const viewportCenter = fittedProjection.invert?.([
        containerWidth / 2,
        containerHeight / 2,
      ]);

      if (!viewportCenter || !isFinite(viewportCenter[0]) || !isFinite(viewportCenter[1])) {
        return DEFAULT_CONFIG;
      }

      // Validate scale
      if (!isFinite(scale)) {
        return DEFAULT_CONFIG;
      }

      // Clamp scale to reasonable bounds
      const clampedScale = Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);

      return {
        scale: clampedScale,
        center: viewportCenter as [number, number],
      };
    } catch {
      return DEFAULT_CONFIG;
    }
  }, [geoData, countryCodes, containerWidth, containerHeight, enabled]);

  return {
    projectionConfig,
    isLoading,
  };
}
